import clsx from 'clsx';
import {  __, _x  } from '@wordpress/i18n';
import { useEffect, useRef, useState } from 'react';
import { useBlockProps, InnerBlocks,BlockControls } from '@wordpress/block-editor';
import {
	arrowDown, arrowLeft, arrowRight, arrowUp, chevronDown,chevronLeft, 
  chevronRight, chevronRightSmall, chevronUp,chevronUpDown, lock
} from '@wordpress/icons';
import PanelSettings from './advance';
import Controls from './controls.js';
import { VayuBlocksDevice } from '@vayu-blocks/device-control';
import {  VayuBlocksUniqueId, borderStyle, borderRadiusStyle, 
          boxShadow,VayuBlocksCssJustify, dimensions, VayuBlocksTypography } from "@vayu-blocks/global";
import {VayuBlockAdvanceStyle} from '@vayu-blocks/advance-panel/style.js';
import {CopyPasteStyles} from '@vayu-blocks';
import { VideoHtml, MouseFollower } from '@vayu-blocks';

const Edit = ({attributes, setAttributes, clientId, isSelected}) => {

    if ( ! attributes.id ) {
		  setAttributes( { id: clientId } );
	  }

    VayuBlocksUniqueId({ 
      setAttributes: setAttributes, 
      clientId: clientId
    });

    const deviceView = VayuBlocksDevice();

    const { mergedStyles, handleMouseEnter, handleMouseLeave,responsiveClasses, hoverAnimationClass } = VayuBlockAdvanceStyle(attributes);

    const [isHovered, setIsHovered] = useState(false);
    const [isUnfolded, setIsUnfolded] = useState(false);

    useEffect(() => {
        if (attributes?.foldicon === 'fold') {
            setIsUnfolded(true);
        } else if (attributes?.foldicon === 'unfold') {
            setIsUnfolded(false);
        }
    }, [attributes?.foldicon]);

    const advanceStyles = {
      '--fade-height':attributes.contentfade?.[deviceView],
      '--fold-height':attributes.contentheight?.[deviceView],
      '--content-gradient': `linear-gradient(0deg, ${attributes.contentGradientbo || 'transparent'} 10%, ${attributes.contentGradient || 'transparent'} 100%)`,
      '--icon-size':attributes.typography?.fontSize?.[deviceView],
      '--btn-color':attributes.btnClr,
      '--btn-hvr-color':attributes.btnHvrClr,
      '--fold-transition': attributes.trans ? `${attributes.transition}s` : '0s',
      '--fold-transition-function':attributes.trans ? attributes.foldTrans : 'ease',
      '--unfolded-height': `${attributes.maxHeight}px` || '1000px'
    }

    const omitBy = (object, condition) => (
      Object.fromEntries(
          Object.entries(object).filter(([key, value]) => !condition(value))
        )
    );

    const style = omitBy({
        ...advanceStyles,
        ...mergedStyles
    }, x => x?.includes?.( 'undefined' ));  

    const blockProps = useBlockProps({
        className: clsx('vayu_blocks_unfold_wrap', hoverAnimationClass, responsiveClasses),
        style
    });

    const unfoldBlockProps = {
      ...blockProps,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    };

    const btnWarpStyles = {
      ...VayuBlocksCssJustify(attributes.align, deviceView),
    }    

    const btnStyles = {
      padding: dimensions(attributes.PaddingBtn, deviceView),
      margin: dimensions(attributes.MarginBtn, deviceView),
      color:attributes.btnClr,
      background:attributes.backgroundBtncolor,
      ...VayuBlocksTypography(attributes.typography, deviceView),
      ...boxShadow(attributes.btnShadow, deviceView),
      ...borderStyle(attributes.btnBorder, deviceView),
      ...borderRadiusStyle(attributes.btnRadius, deviceView),
    }

    const btnHvrStyles = {
        color:attributes.btnHvrClr,
        background:attributes.backgroundBtnhoverColor,
        ...boxShadow(attributes.btnShadowHover, deviceView, attributes.btnShadow),
        ...borderStyle(attributes.btnBorderHover, deviceView, attributes.btnBorder),
        ...borderRadiusStyle(attributes.btnRadiusHover, deviceView, attributes.btnRadius),
    }

    const filteredBtnStyles = omitBy(btnHvrStyles, value => value === undefined || value === null);
    
    const btnStyle = {
      ...btnStyles,
      padding: dimensions(attributes.PaddingBtn, deviceView),
      ...(isHovered ? filteredBtnStyles : {}),
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    };

    const unfold_content_template = [
      [
          'vayu-blocks/advance-container',
          {
              className: 'vayu_blocks_unfold_content',
          },
          [
              [
                  'vayu-blocks/advance-heading',
                  {
                      fontSize:16,
                      fontSizeUnit:'px',
                      content: 'Neque porro quisquam est qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit.. Neque porro quisquam est qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit.. Neque porro quisquam est qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit.. Neque porro quisquam est qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit...',
                      tag: 'p',
                  }
              ]
          ]
      ]
    ];

    const foldTxt = attributes.foldBtnTxt || __('', 'vayu-blocks');
    const unfoldTxt = attributes.unfoldBtnTxt || __('', 'vayu-blocks');

    const iconMap = {
      arrowDown,
      arrowLeft,
      arrowRight,
      arrowUp,
      chevronDown,
      chevronLeft,
      chevronRight,
      chevronRightSmall,
      chevronUp,
      chevronUpDown,
      lock
    };

    const foldIcon = iconMap[attributes.selectedFoldIcon] || null;
    const unfoldIcon = iconMap[attributes.selectedUnFoldIcon] || null;

    const parentRef = useRef(null);

    useEffect(() => {
        const parent = parentRef.current;
        if (!parent) return;

        const child = parent.querySelector('.vayu_blocks_unfold_content');
        if (!child) return;

        const updateHeight = () => {
            const rect = child.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(child);

            const marginTop = parseFloat(computedStyle.marginTop);
            const marginBottom = parseFloat(computedStyle.marginBottom);
            const totalHeight = rect.height + marginTop + marginBottom + 100;

            setAttributes({ maxHeight: totalHeight });
        };

        // Run once initially
        updateHeight();

        // Observe changes in size
        const observer = new ResizeObserver(updateHeight);
        observer.observe(child);

        // Clean up on unmount
        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <>

            <BlockControls>
              <CopyPasteStyles attributes={attributes} setAttributes={setAttributes} blockName={'vayu-blocks/unfold'}/>
            </BlockControls>

           {isSelected && attributes.linkEnable && ( <Controls attributes={ attributes } setAttributes={ setAttributes } />)}

            <PanelSettings attributes={attributes} setAttributes={setAttributes} isSelected={isSelected} clientId={clientId}/>

            <div {...unfoldBlockProps}>

              <VideoHtml bg={attributes.advBackground} />

              <div 
              className="unfold-inner"
              style={{cursor: attributes.contentShowEvent === 'contentclick' || attributes.contentShowEvent === 'contenthover'? 'pointer' : 'auto'}}
              onClick={(e) => {
                if (attributes.contentShowEvent === 'contentclick') {
                  e.stopPropagation();
                  setIsUnfolded(!isUnfolded);
                }
              }}
              onMouseEnter={() => {
                if (attributes.contentShowEvent === 'contenthover') {
                  setIsUnfolded(true);
                }
              }}
              onMouseLeave={() => {
                if (attributes.contentShowEvent === 'contenthover') {
                  setIsUnfolded(false);
                }
              }}
            >

              <div className={`vb-bg-unfold-full-button ${isUnfolded ? 'unfolded' : 'folded'}`}>
                <div ref={parentRef} className={`unfold-content ${isUnfolded ? 'unfolded' : 'folded'}`} 
                >
                  <InnerBlocks template={unfold_content_template} />
                </div>
                {/* Conditional button rendering */}
                {(!attributes.hideUnfoldButton || attributes.contentShowEvent == 'buttonclick') && (
                  <div className="unfold-content-btn" style = {btnWarpStyles}>
                    <div
                      className="unfold-button" style = {{ ...btnStyle }}
                      onClick={() => {if(attributes.contentShowEvent === 'buttonclick') {
                          setIsUnfolded(!isUnfolded);}
                      }}
                      role="button"
                      aria-expanded={isUnfolded}
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                      >
                      {isUnfolded ? (
                        foldIcon && <span className="btn-fold-icon">{foldIcon}</span>
                      ) : (
                        unfoldIcon && <span className="btn-unfold-icon">{unfoldIcon}</span>
                      )}
                      <span>{isUnfolded ? foldTxt : unfoldTxt}</span>
                    </div>
                  </div>
                )}
            </div>

              </div>

              <MouseFollower value={attributes.advFollower} clientId={clientId}/>

            </div>
        </>
      );
};
export default Edit;